import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenService } from '@src/app/core/authen/authen.service';
import { EventPaymentService } from '@src/app/core/http/event-payment.service';
import { SessionService } from '@src/app/core/session.service';
import { ToastService } from '@src/app/core/toast.service';
import { StorageEnum } from '@src/app/shared/enums/storage.enum';
import { UrlParam } from '@src/app/shared/enums/url-param.enum';
import { EventInterface } from '@src/app/shared/interfaces/event.interface';
import { TwoFactorVerificationPayload } from '@src/app/shared/interfaces/request/authen.interface';
import { FormUtils } from '@src/app/shared/utils/form-utils';
import { StringUtils } from '@src/app/shared/utils/string-utils';
import { environment } from '@src/environments/environment';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-form-two-factor',
  templateUrl: './form-two-factor.component.html',
})
export class FormTwoFactorComponent implements OnInit, OnDestroy {
  @Input() credentialOTP: TwoFactorVerificationPayload;
  @Input() eventId;

  @Output() loginSuccess = new EventEmitter<void>();
  @Output() toggleLoginForm = new EventEmitter<void>();

  form: FormGroup;
  requesting = false;
  email: string;
  token: string;
  resendCodeTime: number;
  private countdownTimeout: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private toastService: ToastService,
    public authService: AuthenService,
    private eventPayment: EventPaymentService,
    private sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.email = this.sessionService.getCookie(StorageEnum.email);
    this.token = this.sessionService.getCookie(StorageEnum.mfaToken);
    if (!this.email || !this.token) {
      this.goLoginPage();
      return;
    }
    this.initForm();
    this.startCountdown();
  }

  ngOnDestroy(): void {
    if (this.countdownTimeout) {
      clearTimeout(this.countdownTimeout);
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      otp: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
      email: [this.email, [Validators.required]],
      token: [this.token, [Validators.required]],
    });
  }

  startCountdown(): void {
    this.resendCodeTime = 30;
    this.countdown();
  }

  private countdown(): void {
    if (this.resendCodeTime > 0) {
      this.countdownTimeout = setTimeout(() => {
        this.resendCodeTime--;
        this.countdown();
      }, 1000);
    }
  }

  verify(): void {
    FormUtils.maskFormAsTouched(this.form);
    if (this.form.invalid) {
      this.toastService.showError('UI.Toast.CheckMandatoryField', '', 5000);
      return;
    }

    this.requesting = true;
    const eventDateTime = StringUtils.getParamFromUrl(UrlParam.EventDateTime);
    const payload = this.form.value;

    this.authService
      .verifyTwoFactor(payload)
      .pipe(
        finalize(() => {
          this.requesting = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          this.toastService.showSuccess('UI.Toast.VerifySuccess', '', 5000);
          this.sessionService.deleteGlobalFilterCookies();
          this.loginSuccess.next();
          if (this.eventId) {
            this.eventPayment.handleEventPayment({
              id: this.eventId,
              eventDateTime,
            } as EventInterface);
          }
        },
        error: (error) => {
          this.requesting = false;
          this.toastService.showError('UI.Toast.VerifyFailed', '', 5000);
        },
      });
  }

  resendCode(): void {
    if (this.resendCodeTime > 0) {
      return;
    }

    this.requesting = true;
    const payload = this.form.value;
    this.authService
      .resendOTP(payload.email, payload.token)
      .pipe(
        finalize(() => {
          this.requesting = false;
          this.startCountdown();
        })
      )
      .subscribe({
        next: (res: any) => {
          this.toastService.showSuccess('UI.Toast.ResendCodeSuccess', '', 5000);
          this.form.patchValue({
            token: res.token,
          });
        },
        error: (error) => {
          this.toastService.showError('UI.Toast.ResendCodeFailed', '', 5000);
        },
      });
  }

  back(): void {
    this.toggleLoginForm.emit();
  }

  goLoginPage(): void {
    const url = environment.jipUrl.login;
    FormUtils.navigateTo(url);
  }
}
